import produce from 'immer';
import {
    IContestInitialState,
    ISubmisisonModalPayload,
    types,
    IGetUnsubmittedProjects,
    IContestManageResponse,
    Juror,
    ICampaignSaleItem,
} from './types';
import {
    IContest,
    IContestSubmissionsWithVotes,
    IGenericDocumentModifier,
    ISubmission,
    IUserSummary,
} from '../../types/global/helper';
import { types as projectTypes } from '../project/types';
import { types as EventTypes } from '../events/types';
import { types as requestTypes } from '../requests/types';
import { IJuryDuties, types as juryTypes } from '../jury/types';
import { updateCategoryPricing } from '../../utils/discount.helper';
import { isEmpty } from '../../utils/lodash';

const initialState: IContestInitialState = {
    assigningPrize: false,
    availableFilters: [],
    castingVote: false,
    contestErr: null,
    contests: [],
    contestSubmissions: [],
    creatingBookmark: false,
    currentUnsubmittedProjectsPage: 1,
    deletingBookmark: false,
    gettingBookmarks: false,
    gettingContests: false,
    gettingRecommendations: false,
    gettingS3Images: false,
    gettingSubmissions: false,
    gettingUnSubmittedProjects: false,
    initializingVote: false,
    isSubmisisonModalOpen: false,
    loading: false,
    loadingSubmissions: false,
    markedForDelete: [],
    recommendations: [],
    redeemableCodes: [],
    rescindingSubmission: false,
    s3Images: [],
    scoreBoard: [],
    searchingSubmissions: false,
    selectedContestAwardCategories: [],
    selectedContestPrizeWinners: [],
    similarContest: [],
    startingIndex: 0,
    submissionsContest: null,
    submissionsError: null,
    submittingProject: false,
    unsubmittedProjects: [],
    userBookmarks: [],
    userContests: [],
    userSubmissions: [],
    votes: [],
    // Video Management State
    preselectionSubmissions: [],
    gettingPreselectionSubmissions: false,
    preselectionPagination: {
        currentResults: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        limit: 0,
        page: 0,
        totalCount: 0,
        totalPages: 0,
    },
    preselectionError: null,
    selectedVideos: [],
    videoManagementFilters: {
        page: 1,
        limit: 9,
        category: [],
        searchTerm: '',
        selection_state: '',
        selection_email_sent: '',
        pre_selection_completed: '',
        sort: 'total_average',
        order: 'desc',
    },
    sendingSelectionEmail: false,
    contestInFocusForToken: null,
};

const reducer = (state = initialState, action: any) => {
    const { type, payload } = action;
    switch (type) {
        case types.GET_RECOMMENDATION:
            return produce(state, draftState => {
                draftState.gettingRecommendations = true;
            });
        case types.GET_RECOMMENDATION_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingRecommendations = false;
                draftState.recommendations = payload;
            });
        case types.GET_RECOMMENDATION_FAIL:
            return produce(state, draftState => {
                draftState.gettingRecommendations = false;
                draftState.contestErr = payload;
            });
        case types.GET_BOOKMARK:
            return produce(state, draftState => {
                draftState.gettingBookmarks = true;
            });
        case types.GET_BOOKMARK_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingBookmarks = false;
                draftState.userBookmarks = payload;
            });
        case types.GET_BOOKMARK_FAIL:
            return produce(state, draftState => {
                draftState.gettingBookmarks = false;
                draftState.contestErr = payload;
            });
        case types.GET_MY_SUBMISSIONS:
            return produce(state, draftState => {
                draftState.gettingSubmissions = true;
            });
        case types.GET_MY_SUBMISSIONS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingSubmissions = false;
                draftState.userSubmissions = payload;
            });
        case types.GET_MY_SUBMISSIONS_FAIL:
            return produce(state, draftState => {
                draftState.gettingSubmissions = false;
                draftState.contestErr = payload;
            });
        case types.GET_CONTEST:
            return produce(state, draftState => {
                draftState.selectedContest = undefined;
                draftState.loading = true;
            });
        case types.GET_CONTEST_SUCCESS:
            return produce(state, draftState => {
                const { data } = payload;
                draftState.loading = false;
                draftState.selectedContest = data;
            });
        case types.GET_CONTEST_FAIL:
            return produce(state, draftState => {
                draftState.loading = false;
                draftState.contestErr = payload;
            });
        case types.GET_CONTESTS:
            return produce(state, draftState => {
                draftState.loading = true;
                draftState.references = undefined;
            });
        case types.GET_CONTESTS_SUCCESS:
            return produce(state, draftState => {
                const { data, isFilitered } = payload;
                draftState.loading = false;
                if (isFilitered) {
                    draftState.similarContest = data;
                } else {
                    draftState.contests = data;
                }
            });
        case types.GET_CONTESTS_FAIL:
            return produce(state, draftState => {
                draftState.loading = false;
                draftState.contestErr = payload;
            });
        case types.SET_SUBMISSION_MODAL_STATE:
            return produce(state, draftState => {
                const { open, selectedCategory } = payload as ISubmisisonModalPayload;
                draftState.isSubmisisonModalOpen = open;
                if (open && selectedCategory) {
                    draftState.selectedCategory = selectedCategory;
                } else {
                    draftState.selectedCategory = undefined;
                }
                /** Remove all discounts */
                draftState.selectedDiscount = undefined;
            });
        case types.SET_CONTEST_FOR_SUBMISSION:
            return produce(state, draftState => {
                if (!draftState.autoPublishAndSubmit) {
                    draftState.selectedContestForSubmisison = payload;
                    draftState.selectedDiscount = undefined;
                }
            });
        case types.SUBMIT_PROJECT:
            return produce(state, draftState => {
                draftState.submittingProject = true;
            });
        case types.SUBMIT_PROJECT_SUCCESS:
            return produce(state, draftState => {
                draftState.userSubmissions = [...(draftState.userSubmissions ?? []), payload];
                draftState.submittingProject = false;
                /** Reset auto submit */
                draftState.autoPublishAndSubmit = false;
                draftState.selectedContestForSubmisison = undefined;
                /** Remove the unsubmitted project from the list for user */
                draftState.unsubmittedProjects = draftState.unsubmittedProjects.filter(
                    project => project._id !== payload.project._id,
                );
            });
        case types.SUBMIT_PROJECT_FAIL:
            return produce(state, draftState => {
                draftState.submittingProject = false;
                draftState.contestErr = payload;
                draftState.autoPublishAndSubmit = false;
                draftState.selectedContestForSubmisison = undefined;
            });
        case types.RESCIND_SUBMISSION:
            return produce(state, draftState => {
                draftState.rescindingSubmission = true;
            });
        case types.RESCIND_SUBMISSION_SUCCESS:
            return produce(state, draftState => {
                const { _id } = payload as ISubmission;
                draftState.userSubmissions = draftState.userSubmissions.filter(submission => submission._id !== _id);
                draftState.rescindingSubmission = false;
            });
        case types.RESCIND_SUBMISSION_FAIL:
            return produce(state, draftState => {
                draftState.contestErr = payload;
                draftState.rescindingSubmission = false;
            });
        // case types.RESET_CONTEST_DATA:
        //     return restoreInitialState(state, initialState) as IContestInitialState;
        case types.INITIATE_2FA_FOR_VOTE:
            return produce(state, draftState => {
                draftState.initializingVote = true;
            });
        case types.RESET_VOTE:
            return produce(state, draftState => {
                draftState.initializingVote = false;
            });
        case types.INITIATE_2FA_FOR_VOTE_SUCCESS:
            return produce(state, draftState => {
                draftState.initializingVote = false;
                /** Increase the Vote for the submision here */
                const { vote, message } = payload;
                const currentSubmissionIndex = draftState.scoreBoard.findIndex(
                    submission => submission._id === vote.submission,
                );
                if (currentSubmissionIndex > -1 && message.startsWith('Vote cast successfully')) {
                    draftState.scoreBoard[currentSubmissionIndex].total_votes =
                        draftState.scoreBoard[currentSubmissionIndex].total_votes + 1;
                }
            });
        case types.INITIATE_2FA_FOR_VOTE_FAIL:
            return produce(state, draftState => {
                draftState.voteErrMsg = payload;
                draftState.initializingVote = false;
            });
        case types.VOTE_FOR_SUBMISSION:
            return produce(state, draftState => {
                draftState.castingVote = true;
            });
        case types.VOTE_FOR_SUBMISSION_SUCCESS:
            return produce(state, draftState => {
                draftState.votes?.push(payload);
                draftState.castingVote = false;
            });
        case types.VOTE_FOR_SUBMISSION_FAIL:
            return produce(state, draftState => {
                draftState.voteErrMsg = payload;
                draftState.castingVote = false;
            });
        case types.CREATE_BOOKMARK:
            return produce(state, draftState => {
                const { details } = payload;
                const tempPayload: any = {
                    id: 'temporary',
                    details,
                    deleted: false,
                };
                draftState.userBookmarks = draftState.userBookmarks ?? [];
                draftState.userBookmarks.push(tempPayload as any);
                draftState.creatingBookmark = true;
                if (draftState.selectedContest) {
                    draftState.selectedContest['user_bookmark'] = tempPayload;
                    draftState.selectedContest['has_user_bookmarked'] = true;
                }
            });
        case types.CREATE_BOOKMARK_SUCCESS:
            return produce(state, draftState => {
                draftState.creatingBookmark = false;
                const index = draftState.userBookmarks.findIndex(e => e.id === 'temporary');
                if (index > -1) {
                    draftState.userBookmarks[index] = payload;
                }
                if (draftState.selectedContest) {
                    draftState.selectedContest['user_bookmark'] = payload;
                    draftState.selectedContest['has_user_bookmarked'] = true;
                }
            });
        case types.CREATE_BOOKMARK_FAIL:
            return produce(state, draftState => {
                /** Passing documentId from SAGA as the model being saved */
                const { documentId } = payload;
                draftState.creatingBookmark = false;
                draftState.userBookmarks = draftState.userBookmarks.filter(
                    e => e.id !== 'temporary' && e.details.model !== documentId,
                );
                draftState.contestErr = payload;
                if (draftState.selectedContest) {
                    draftState.selectedContest['user_bookmark'] = undefined;
                    draftState.selectedContest['has_user_bookmarked'] = false;
                }
            });
        case 'REFRESH_TOKEN':
        case 'ENQUEUE_FAILED_ACTIONS':
            return produce(state, draftState => {
                draftState.creatingBookmark = false;
            });
        case types.DELETE_BOOKMARK:
            return produce(state, draftState => {
                draftState.deletingBookmark = true;
                if (draftState.selectedContest) {
                    draftState.selectedContest['user_bookmark'] = undefined;
                    draftState.selectedContest['has_user_bookmarked'] = false;
                }
            });
        case types.DELETE_BOOKMARK_SUCCESS:
            return produce(state, draftState => {
                draftState.deletingBookmark = false;
                if (draftState.selectedContest) {
                    draftState.selectedContest['user_bookmark'] = undefined;
                    draftState.selectedContest['has_user_bookmarked'] = false;
                }
            });
        case types.DELETE_BOOKMARK_FAIL:
            return produce(state, draftState => {
                const { additionalPayload } = payload;
                draftState.deletingBookmark = false;
                draftState.contestErr = payload;
                if (draftState.selectedContest) {
                    draftState.selectedContest['user_bookmark'] = additionalPayload;
                    draftState.selectedContest['has_user_bookmarked'] = true;
                }
            });
        case 'LOGOUT_SUCCESS':
            return produce(state, draftState => {
                draftState.userBookmarks = [];
                draftState.selectedCategory = undefined;
            });
        case types.GET_USER_CONTEST:
            return produce(state, draftState => {
                if (!payload.skipRefresh) {
                    draftState.loading = true;
                }
            });
        case types.GET_USER_CONTEST_SUCCESS:
            return produce(state, draftState => {
                draftState.loading = false;
                draftState.selectedContestForMetrics = payload;
            });
        case types.GET_USER_CONTEST_FAIL:
            return produce(state, draftState => {
                draftState.loading = false;
            });
        case types.SET_CONTEST_FOR_EDIT:
            return produce(state, draftState => {
                draftState.contestInCreation = payload;
            });
        case types.GET_USER_CONTESTS:
            return produce(state, draftState => {
                draftState.gettingContests = true;
            });
        case types.GET_USER_CONTESTS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingContests = false;
                draftState.userContests = payload;
            });
        case types.GET_USER_CONTESTS_FAIL:
            return produce(state, draftState => {
                draftState.gettingContests = false;
                draftState.contestErr = payload;
            });
        case types.CREATE_CONTEST:
            return produce(state, draftState => {
                draftState.creatingContest = true;
            });
        case types.CREATE_CONTEST_SUCCESS:
            return produce(state, draftState => {
                draftState.creatingContest = false;
                draftState.userContests = [payload, ...draftState.userContests];
            });
        case types.CREATE_CONTEST_FAIL:
            return produce(state, draftState => {
                draftState.creatingContest = false;
                draftState.contestErr = payload;
            });
        case types.UPDATE_CONTEST:
            return produce(state, draftState => {
                draftState.creatingContest = true;
            });
        case types.UPDATE_CONTEST_SUCCESS:
        case types.PUBLISH_CONTEST_SUCCESS:
        case types.UNPUBLISH_CONTEST_SUCCESS:
            return produce(state, draftState => {
                draftState.creatingContest = false;
                draftState.loading = false;
                const contestInEdit = draftState.userContests.findIndex(contest => contest._id === payload._id);
                if (contestInEdit > -1) {
                    draftState.userContests[contestInEdit] = payload;
                }
                if (draftState.selectedContestForMetrics) {
                    draftState.selectedContestForMetrics.status = payload.status;
                }

                /** Check for managedContest */
                if (draftState.managedContest && draftState.managedContest.contest) {
                    Object.keys(payload).forEach(key => {
                        const currentKey = key as keyof IContest;
                        if (draftState.managedContest && currentKey in draftState.managedContest.contest) {
                            draftState.managedContest.contest = {
                                ...draftState.managedContest.contest,
                                [currentKey]: payload[currentKey],
                            };
                        } else if (draftState.managedContest) {
                            draftState.managedContest.contest = {
                                ...draftState.managedContest.contest,
                                [currentKey]: payload[currentKey],
                            };
                        }
                    });
                }
            });
        case types.UPDATE_CONTEST_FAIL:
            return produce(state, draftState => {
                draftState.creatingContest = false;
                draftState.contestErr = payload;
            });
        case types.DELETE_CONTEST:
            return produce(state, draftState => {
                draftState.deletingContest = true;
            });
        case types.DELETE_CONTEST_SUCCESS:
            return produce(state, draftState => {
                draftState.deletingContest = false;
                const { documentId } = payload as IGenericDocumentModifier;
                draftState.userContests = draftState.userContests.filter(contest => contest._id !== documentId);
            });
        case types.DELETE_CONTEST_FAIL:
            return produce(state, draftState => {
                draftState.deletingContest = false;
            });
        case types.PUBLISH_CONTEST:
        case types.UNPUBLISH_CONTEST:
            return produce(state, draftState => {
                draftState.loading = true;
            });
        case types.PUBLISH_CONTEST_FAIL:
        case types.UNPUBLISH_CONTEST_FAIL:
            return produce(state, draftState => {
                draftState.loading = false;
            });
        case types.GET_HOST:
            return produce(state, draftState => {});
        case types.GET_HOST_SUCCESS:
            return produce(state, draftState => {});
        case types.GET_HOST_FAIL:
            return produce(state, draftState => {});
        case types.GET_ALL_HOSTS:
            return produce(state, draftState => {});
        case types.GET_ALL_HOSTS_SUCCESS:
            return produce(state, draftState => {});
        case types.GET_ALL_HOSTS_FAIL:
            return produce(state, draftState => {});
        case types.CREATE_HOST:
            return produce(state, draftState => {});
        case types.CREATE_HOST_SUCCESS:
            return produce(state, draftState => {});
        case types.CREATE_HOST_FAIL:
            return produce(state, draftState => {});
        case types.CREATE_PARTNER:
            return produce(state, draftState => {});
        case types.CREATE_PARTNER_SUCCESS:
            return produce(state, draftState => {});
        case types.CREATE_PARTNER_FAIL:
            return produce(state, draftState => {});
        case types.UPDATE_HOST:
            return produce(state, draftState => {});
        case types.UPDATE_HOST_SUCCESS:
            return produce(state, draftState => {});
        case types.UPDATE_HOST_FAIL:
            return produce(state, draftState => {});
        case types.DELETE_HOST:
            return produce(state, draftState => {});
        case types.DELETE_HOST_SUCCESS:
            return produce(state, draftState => {});
        case types.DELETE_HOST_FAIL:
            return produce(state, draftState => {});
        case types.GET_PARTNER:
            return produce(state, draftState => {});
        case types.GET_PARTNER_SUCCESS:
            return produce(state, draftState => {});
        case types.GET_PARTNER_FAIL:
            return produce(state, draftState => {});
        case types.DELETE_PARTNER:
            return produce(state, draftState => {});
        case types.DELETE_PARTNER_SUCCESS:
            return produce(state, draftState => {});
        case types.DELETE_PARTNER_FAIL:
            return produce(state, draftState => {});
        case types.SEARCH_HOST:
            return produce(state, draftState => {
                draftState.searchingHost = true;
                draftState.hostSearchResult = [];
            });
        case types.SEARCH_HOST_SUCCESS:
            return produce(state, draftState => {
                draftState.searchingHost = false;
                draftState.hostSearchResult = payload;
            });
        case types.SEARCH_HOST_FAIL:
            return produce(state, draftState => {
                draftState.searchingHost = false;
            });
        case types.SEARCH_PARTNER:
            return produce(state, draftState => {
                draftState.searchingPartner = true;
                draftState.partnerSearchResult = [];
            });
        case types.SEARCH_PARTNER_SUCCESS:
            return produce(state, draftState => {
                draftState.searchingPartner = false;
                draftState.partnerSearchResult = payload;
            });
        case types.SEARCH_PARTNER_FAIL:
            return produce(state, draftState => {
                draftState.searchingPartner = false;
            });
        case types.ASSIGN_REDEEMABLE_CODE:
            return produce(state, draftState => {
                draftState.assigningCode = true;
            });
        case types.ASSIGN_REDEEMABLE_CODE_SUCCESS:
            return produce(state, draftState => {
                draftState.redeemableCodes?.push(payload);
                draftState.assigningCode = false;
            });
        case types.ASSIGN_REDEEMABLE_CODE_FAIL:
            return produce(state, draftState => {
                draftState.assigningCode = false;
                draftState.contestErr = payload;
            });
        case types.GET_REDEEMABLE_CODE:
            return produce(state, draftState => {
                draftState.fetchingAssignedCodes = true;
            });
        case types.GET_REDEEMABLE_CODE_SUCCESS:
            return produce(state, draftState => {
                draftState.redeemableCodes = payload;
                draftState.fetchingAssignedCodes = false;
            });
        case types.GET_REDEEMABLE_CODE_FAIL:
            return produce(state, draftState => {
                draftState.contestErr = payload;
                draftState.fetchingAssignedCodes = false;
            });
        case types.SET_VOTING_FLOW:
            return produce(state, draftState => {
                const { submissions, showVotingModal } = payload;
                draftState.submissionsToVoteFor = submissions;
                draftState.showVotingBooth = showVotingModal;
            });
        case types.SET_VOTING_2FA:
            return produce(state, draftState => {
                draftState.showVoting2Fa = payload;
            });
        case types.SET_VOTING_2FA_STARTING_INDEX:
            return produce(state, draftState => {
                draftState.startingIndex = payload;
            });
        case types.GET_AWARD_CATEGORIES_FOR_CONTEST:
        case types.GET_AWARDED_PRIZES_FOR_CONTEST:
            return produce(state, draftState => {
                draftState.gettingContestAwardCategories = true;
            });
        case types.GET_AWARD_CATEGORIES_FOR_CONTEST_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingContestAwardCategories = false;
                draftState.selectedContestAwardCategories = payload;
            });
        case types.GET_AWARD_CATEGORIES_FOR_CONTEST_FAIL:
        case types.GET_AWARDED_PRIZES_FOR_CONTEST_FAIL:
            return produce(state, draftState => {
                draftState.gettingContestAwardCategories = false;
                draftState.contestErr = payload;
            });
        case types.GET_AWARDED_PRIZES_FOR_CONTEST_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingContestAwardCategories = true;
                draftState.selectedContestPrizeWinners = payload;
            });
        case types.AWARD_PRIZE:
            return produce(state, draftState => {
                draftState.assigningPrize = true;
            });
        case types.AWARD_PRIZE_SUCCESS:
            return produce(state, draftState => {
                draftState.assigningPrize = false;
                const { award, contest } = payload;
                draftState.selectedContestPrizeWinners.push(award);

                if (draftState.managedContest) {
                    if (
                        draftState.managedContest?.awards.awards &&
                        !isEmpty(draftState.managedContest?.awards.awards)
                    ) {
                        draftState.managedContest.awards.awards = [...draftState.managedContest.awards.awards, award];
                    } else {
                        draftState.managedContest.awards.awards = [award];
                    }
                }
                /** Check for managedContest */
                if (draftState.managedContest && draftState.managedContest.contest) {
                    draftState.managedContest.contest = {
                        ...draftState.managedContest.contest,
                        amount_available: contest.amount_available,
                    };
                }
            });
        case types.AWARD_PRIZE_FAIL:
            return produce(state, draftState => {
                draftState.assigningPrize = false;
                draftState.contestErr = payload;
            });
        case types.SET_SUBMISSION_PROMPT:
            return produce(state, draftState => {
                draftState.promptSubmission = payload;
            });
        case types.SET_AUTO_PUBLISH_AND_SUBMIT:
            return produce(state, draftState => {
                draftState.promptSubmission = false;
                draftState.autoPublishAndSubmit = payload;
            });
        case types.GET_ALL_SUBMISSIONS:
            return produce(state, draftState => {
                draftState.fetchingScoreBoard = true;
            });
        case types.GET_ALL_SUBMISSIONS_SUCCESS:
            return produce(state, draftState => {
                const { details, submissions } = payload;
                const { page } = details;
                if (page && page > 1) {
                    draftState.scoreBoard = [...draftState.scoreBoard, ...submissions];
                } else {
                    draftState.scoreBoard = submissions;
                }
                draftState.fetchingScoreBoard = false;
            });
        case types.GET_ALL_SUBMISSIONS_FAIL:
            return produce(state, draftState => {
                draftState.fetchingScoreBoard = false;
                draftState.contestErr = payload;
            });
        case types.CREATE_SCORE_CATEGORY:
            return produce(state, draftState => {
                draftState.creatingScorecategory = true;
            });
        case types.CREATE_SCORE_CATEGORY_SUCCESS:
            return produce(state, draftState => {
                draftState.creatingScorecategory = false;
                if (draftState.contestInCreation) {
                    draftState.contestInCreation?.score_criteria!.push(payload);
                }
            });
        case types.CREATE_SCORE_CATEGORY_FAIL:
            return produce(state, draftState => {
                draftState.creatingScorecategory = false;
                draftState.contestErr = payload;
            });
        case types.SET_FOCUS_CAMPAIGN:
            return produce(state, draftState => {
                draftState.focusCampaign = payload;
            });
        case types.GET_S3_IMAGES_SUCCESS:
            return produce(state, draftState => {
                draftState.s3Images = payload;
                draftState.gettingS3Images = false;
            });
        case types.SET_EMAIL_IMAGE:
            return produce(state, draftState => {
                draftState.emailImage = payload;
            });
        case types.GET_S3_IMAGES_FAIL:
            return produce(state, draftState => {
                draftState.gettingS3Images = false;
            });
        case types.GET_S3_IMAGES:
            return produce(state, draftState => {
                draftState.gettingS3Images = true;
            });
        case types.GET_CONTEST_REFERENCES:
            return produce(state, draftState => {
                draftState.gettingReferences = true;
            });
        case types.GET_CONTEST_REFERENCES_SUCCESS:
            return produce(state, draftState => {
                draftState.references = payload;
                draftState.gettingReferences = false;
            });
        case types.GET_CONTEST_REFERENCES_FAIL:
            return produce(state, draftState => {
                draftState.gettingReferences = false;
            });
        case types.GET_USERS_UNSUBMITTED_PROJECTS:
            return produce(state, draftState => {
                const { page } = payload as IGetUnsubmittedProjects;
                /** This means we are fetching more */
                if (page && page < 1) {
                    draftState.gettingUnSubmittedProjects = true;
                }
            });
        case types.GET_USERS_UNSUBMITTED_PROJECTS_SUCCESSS:
            return produce(state, draftState => {
                draftState.gettingUnSubmittedProjects = false;
                const { current_page, results } = payload;
                if (current_page && current_page > 1) {
                    draftState.unsubmittedProjects = [...draftState.unsubmittedProjects, ...results];
                } else {
                    draftState.unsubmittedProjects = results;
                }
                draftState.fetchingScoreBoard = false;
                draftState.currentUnsubmittedProjectsPage = current_page;
            });
        case types.GET_USERS_UNSUBMITTED_PROJECTS_FAIL:
            return produce(state, draftState => {
                draftState.gettingUnSubmittedProjects = false;
                draftState.unsubmittedProjectErrMsg = payload;
            });
        case types.SET_CATEGORY_FOR_SUBMISSION:
            return produce(state, draftState => {
                draftState.selectedCategoryForSubmission = payload;
                draftState.selectedCategory = payload;
                draftState.initialSelectedCategory = undefined;
                draftState.selectedDiscount = undefined;
            });

        // Video Management Cases
        case types.GET_PRESELECTION_SUBMISSIONS:
            return produce(state, draftState => {
                draftState.gettingPreselectionSubmissions = true;
                draftState.preselectionError = null;
            });
        case types.GET_PRESELECTION_SUBMISSIONS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingPreselectionSubmissions = false;
                draftState.preselectionSubmissions = payload.data;
                draftState.preselectionPagination = payload.pagination;
            });
        case types.GET_PRESELECTION_SUBMISSIONS_FAIL:
            return produce(state, draftState => {
                draftState.gettingPreselectionSubmissions = false;
                draftState.preselectionError = payload;
            });

        case types.SEND_SELECTION_EMAIL:
            return produce(state, draftState => {
                draftState.sendingSelectionEmail = true;
            });
        case types.SEND_SELECTION_EMAIL_SUCCESS:
            return produce(state, draftState => {
                draftState.sendingSelectionEmail = false;
                draftState.selectedVideos = [];
                const { completedIds } = payload.data;
                if (completedIds) {
                    completedIds.forEach((submitId: string) => {
                        const selected = draftState.preselectionSubmissions.find(sub => sub._id === submitId);
                        if (selected) {
                            selected.selection_email_sent = true;
                        }
                    });
                }
            });
        case types.SEND_SELECTION_EMAIL_FAIL:
            return produce(state, draftState => {
                draftState.sendingSelectionEmail = false;
                draftState.contestErr = payload;
            });

        case types.UPDATE_VIDEO_MANAGEMENT_FILTERS:
            return produce(state, draftState => {
                draftState.videoManagementFilters = {
                    ...draftState.videoManagementFilters,
                    ...payload.filters,
                };
            });

        case types.SET_SELECTED_VIDEOS:
            return produce(state, draftState => {
                draftState.selectedVideos = payload.videoIds;
            });

        case types.CLEAR_SELECTED_VIDEOS:
            return produce(state, draftState => {
                draftState.selectedVideos = [];
            });
        case types.PRESELECT_SUBMISSIONS_SUCCESS:
            return produce(state, draftState => {
                const { submissions } = payload.data;
                if (submissions) {
                    submissions.forEach((submission: IContestSubmissionsWithVotes) => {
                        const selected = draftState.preselectionSubmissions.find(sub => sub._id === submission._id);
                        if (selected) {
                            selected.pre_selection_completed = submission.pre_selection_completed;
                            selected.selection_email_sent = submission.selection_email_sent;
                            selected.selection_state = submission.selection_state;
                        }
                    });
                }
            });
        case projectTypes.GET_PROJECT:
        case projectTypes.GET_PROJECT_DETAILS:
            return produce(state, draftState => {
                draftState.submissionsToVoteFor = [];
            });
        case types.GET_CONTEST_SUBMISSIONS:
            return produce(state, draftState => {
                draftState.searchingSubmissions = false;
                draftState.contestSubmissions = [];
                draftState.submissionsContest = null;
                if (payload.searchTerm) {
                    draftState.searchingSubmissions = true;
                } else {
                    draftState.loadingSubmissions = true;
                }
            });
        case types.GET_CONTEST_SUBMISSIONS_SUCCESS:
            return produce(state, draftState => {
                const { data, availableFilters, contest } = payload;
                draftState.contestSubmissions = data;
                draftState.availableFilters = availableFilters;
                draftState.loadingSubmissions = false;
                draftState.searchingSubmissions = false;
                draftState.submissionsContest = contest;
            });
        case types.GET_CONTEST_SUBMISSIONS_FAIL:
            return produce(state, draftState => {
                draftState.submissionsError = payload;
                draftState.loadingSubmissions = false;
                draftState.searchingSubmissions = false;
            });
        case types.SET_SEARCHING_CONTEST_SUBMISSIONS:
            return produce(state, draftState => {
                draftState.searchingSubmissions = payload;
            });
        case types.GET_MANAGE_CAMPAIGN:
            return produce(state, draftState => {
                draftState.managedContestLoading = true;
                draftState.managedContestError = null;
            });
        case types.GET_MANAGE_CAMPAIGN_SUCCESS:
            return produce(state, draftState => {
                draftState.managedContestLoading = false;
                if (draftState.managedContest) {
                    Object.keys(payload).forEach(key => {
                        if (draftState.managedContest) {
                            draftState.managedContest![key as keyof IContestManageResponse] = payload[key];
                        }
                    });
                } else {
                    draftState.managedContest = payload;
                }
            });
        case EventTypes.CREATE_DISCOUNT_CODE_SUCCESS:
            return produce(state, draft => {
                if (draft.managedContest && !!payload.contest) {
                    draft.managedContest.discounts.discounts.push(payload);
                }
            });
        case EventTypes.UPDATE_DISCOUNT_SUCCESS:
            return produce(state, draft => {
                if (draft.managedContest?.discounts?.discounts) {
                    const index = draft.managedContest.discounts.discounts.findIndex(
                        discount => discount._id === payload._id,
                    );
                    if (index !== -1) {
                        draft.managedContest.discounts.discounts[index] = payload;
                    }
                }
            });
        case EventTypes.DELETE_DISCOUNT_SUCCESS:
            return produce(state, draft => {
                if (draft.managedContest?.discounts?.discounts) {
                    draft.managedContest.discounts.discounts = draft.managedContest.discounts.discounts.filter(
                        discount => discount._id !== payload,
                    );
                }
            });
        case types.UPDATE_CAMPAIGN_AWARDS_SUCCESS:
            return produce(state, draft => {
                if (draft.managedContest?.contest) {
                    const { categories } = payload || {};
                    if (categories) {
                        draft.managedContest.contest.categories = categories;
                    }
                }
            });
        case types.GET_MANAGE_CAMPAIGN_FAIL:
            return produce(state, draftState => {
                draftState.managedContestLoading = false;
                draftState.managedContestError = payload;
            });
        case types.CREATE_AWARD_CATEGORY_SUCCESS:
            return produce(state, draft => {
                if (draft.managedContest?.award_categories) {
                    draft.managedContest.award_categories.categories.push(payload);
                }
            });
        case types.UPDATE_AWARD_CATEGORY_SUCCESS:
            return produce(state, draft => {
                const {
                    body,
                    // payload: initialPayload
                } = payload;
                // const { category } = initialPayload;
                if (draft.managedContest?.award_categories) {
                    const awardCategoryInContextIndex = draft.managedContest?.award_categories.categories.findIndex(
                        a => a._id === payload._id,
                    );
                    if (awardCategoryInContextIndex > -1) {
                        draft.managedContest.award_categories.categories[awardCategoryInContextIndex] = body;
                    }
                }
                /** Update the value for contest categories too */
                // if (draft.managedContest?.contest && draft.managedContest.contest.categories) {
                //     const categoryInContext = draft.managedContest.contest.categories.find(cat => cat._id === category);
                //     if (categoryInContext && categoryInContext.category_awards) {
                //         const currentlyHasAwardIndex = categoryInContext.category_awards?.findIndex(
                //             cat => cat._id === body._id,
                //         );
                //         if (currentlyHasAwardIndex && currentlyHasAwardIndex > -1) {
                //             categoryInContext.category_awards[currentlyHasAwardIndex] = body;
                //         } else {
                //             categoryInContext.category_awards.push(initialPayload);
                //         }
                //     }
                // }
            });
        case types.DELETE_AWARD_CATEGORY_SUCCESS:
            return produce(state, draft => {
                if (draft.managedContest?.award_categories) {
                    const filtered = draft.managedContest?.award_categories.categories.filter(
                        a => a._id !== payload.documentId,
                    );
                    draft.managedContest.award_categories.categories = filtered;
                }
            });
        case juryTypes.GET_JURORS_FOR_CONTEST_SUCCESS:
            return produce(state, draft => {
                if (draft.managedContest && draft.managedContest.jurors) {
                    const parsed = payload.map(
                        (jury: IJuryDuties): Juror => ({
                            _id: jury._id, // assuming IJuryDuties has a unique _id
                            accepted_invite: jury.accepted_invite ?? false,
                            invite_sent: jury.invite_sent ?? true,
                            judged_submissions: 0,
                            judgment_percentage: 0,
                            juror: jury.jury as unknown as IUserSummary,
                            pending_pagination: {
                                currentPage: 0,
                                pageSize: 0,
                                totalPending: 0,
                                hasMore: false,
                                remaining: 0,
                            },
                            pending_submissions: [],
                            total_assigned: 0,
                            jury_settings: jury.jury_settings!,
                        }),
                    );

                    draft.managedContest.jurors = parsed;
                }
            });
        case juryTypes.DELETE_JURY_SUCCESS:
            return produce(state, draft => {
                if (draft.managedContest?.jurors) {
                    const { documentId } = payload;
                    const filtered = draft.managedContest?.jurors.filter(j => j._id !== documentId);
                    draft.managedContest.jurors = filtered;
                }
            });
        case juryTypes.DISTRIBUTE_SUBMISSIONS_TO_JURIES_SUCCESS:
            return produce(state, draft => {
                if (draft.managedContest?.jurors) {
                    const { juryAssignmentBreakdown } = payload || {};
                    if (juryAssignmentBreakdown) {
                        const ids: string[] = Object.keys(juryAssignmentBreakdown);
                        const values: number[] = Object.values(juryAssignmentBreakdown);

                        draft.managedContest.jurors = draft.managedContest.jurors.map(curVal => {
                            const assignmentIndex = ids.findIndex(id => id === curVal._id);

                            if (assignmentIndex !== -1) {
                                const addedAssignments = values[assignmentIndex];

                                const judged = (curVal.judgment_percentage / 100) * curVal.total_assigned;
                                const newTotalAssigned = curVal.total_assigned + addedAssignments;

                                const newJudgmentPercentage =
                                    newTotalAssigned === 0 ? 0 : (judged / newTotalAssigned) * 100;

                                return {
                                    ...curVal,
                                    total_assigned: newTotalAssigned,
                                    judgment_percentage: newJudgmentPercentage,
                                };
                            }

                            return curVal;
                        });
                    }
                }
            });
        case types.SET_DISCOUNT_CODE:
            return produce(state, draftState => {
                draftState.selectedDiscount = payload;
                /** Recalculate the seletedCategory */
                if (draftState.selectedCategory) {
                    const newCategory = updateCategoryPricing(
                        draftState.selectedCategory,
                        payload.value,
                        false,
                        draftState.selectedDiscount?.type === 'fixed',
                    );
                    draftState.initialSelectedCategory = draftState.selectedCategory;
                    draftState.selectedCategory = newCategory;
                }
            });
        case types.CLEAR_DISCOUNT_CODE:
            return produce(state, draftState => {
                /** Recalculate the seletedCategory */
                /** Recalculate the seletedCategory */
                if (draftState.selectedCategory && draftState.initialSelectedCategory && draftState.selectedDiscount) {
                    const initialCatgeory = updateCategoryPricing(
                        draftState.initialSelectedCategory,
                        draftState.selectedDiscount?.value,
                        true,
                        draftState.selectedDiscount?.type === 'fixed',
                    );
                    draftState.selectedCategory = initialCatgeory;
                    draftState.initialSelectedCategory = undefined;
                }
                draftState.selectedDiscount = undefined;
            });
        case requestTypes.UPDATE_REQUEST_SUCCESS:
            return produce(state, draftState => {
                if (draftState.managedContest)
                    draftState.managedContest['contest'].request = payload.deleted ? undefined : payload;
            });
        case types.FUNDING_COMPLETE:
            return produce(state, draftState => {
                if (draftState.managedContest) {
                    const total = payload.items.reduce(
                        (sum: number, item: ICampaignSaleItem) =>
                            sum + (item?.breakdown?.actual_amount ?? item.price) * item.quantity,
                        0,
                    );
                    draftState.managedContest['contest'].funded = true;
                    draftState.managedContest['contest'].amount_available = draftState.managedContest[
                        'contest'
                    ].amount_available += total;
                }
            });
        case types.SET_SELECTED_PROJECT:
            return produce(state, draftState => {
                draftState.selectedProject = payload;
            });
        case types.SET_CONTEST_FOR_TOKENS:
            return produce(state, draftState => {
                draftState.contestInFocusForToken = payload;
            });
        default:
            return state;
    }
};

export { reducer };
